// src/auth/active-session-conflict-dialog.tsx
import { useState } from 'react';
import { ModalYesNo } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from './auth-store';

/**
 * "Ongoing Session" confirm dialog — shown on the deployed manual-login path
 * when GetUserDataToken returns 409 (an active session already exists for this
 * user). The user chooses to take over (release the prior session via
 * UpdateDeviceDetails + re-auth) or cancel (log out locally, stay on /login).
 *
 * Mirrors the PM app's ActiveSessionConflictDialog / Flutter
 * `showActiveSessionPopup`. Mounted once at app root (App.tsx); subscribes to
 * `activeSessionConflict` and opens iff non-null. In local dev the takeover is
 * automatic (no dialog) — see auth-store `login()`.
 */
export function ActiveSessionConflictDialog() {
  const conflict = useAuthStore((s) => s.activeSessionConflict);
  const confirmTakeover = useAuthStore((s) => s.confirmTakeover);
  const cancelTakeover = useAuthStore((s) => s.cancelTakeover);
  const [loading, setLoading] = useState(false);

  async function onConfirm() {
    if (loading) return;
    setLoading(true);
    try {
      await confirmTakeover();
    } finally {
      setLoading(false);
    }
  }

  function onCancel() {
    if (loading) return;
    cancelTakeover();
  }

  return (
    <ModalYesNo
      open={conflict !== null}
      title="Ongoing Session"
      message="There is an active session for this user on another device. Would you like to log it out and continue here?"
      confirmLabel={loading ? 'Logging out other session…' : 'Yes, continue'}
      cancelLabel="No"
      onConfirm={onConfirm}
      onCancel={onCancel}
      loading={loading}
      destructive
    />
  );
}

export default ActiveSessionConflictDialog;
